import { Link } from 'react-router-dom';
import { ArrowRight, Check, Minus, Scale } from 'lucide-react';
import { Button } from '../ui/button';

const POINTS = [
  'Pricing, provinces, languages and EMR compatibility in one view',
  '"Fits your clinic" highlights based on your province, language, EMR and budget',
  "Details taken from each vendor's own website, with gaps marked \"Not yet confirmed\"",
  'Save comparisons to your dashboard and email them to your team',
];

const ROWS = [
  ['Price / provider', ['$', '$$', '$']],
  ['Available in your province', [true, true, false]],
  ['Works with your EMR', [true, false, true]],
  ['Data stored in Canada', [true, true, null]],
  ['French support', [false, true, true]],
];

const Cell = ({ v }) => {
  if (v === true) return <Check className="mx-auto h-4 w-4 text-emerald-600" />;
  if (v === false) return <Minus className="mx-auto h-4 w-4 text-slate-300" />;
  if (v === null) return <span className="text-[10px] text-slate-400">Not yet confirmed</span>;
  return <span className="text-sm font-semibold text-slate-700">{v}</span>;
};

const Preview = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-lg" data-testid="home-compare-preview">
    <div className="grid grid-cols-[1.4fr_repeat(3,1fr)] items-center gap-2 border-b border-slate-100 pb-3">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Example</span>
      {['A', 'B', 'C'].map((x, i) => (
        <div key={x} className={`mx-auto flex h-9 w-9 items-center justify-center rounded-lg text-sm font-bold ${i === 0 ? 'bg-orange-600 text-white ring-2 ring-orange-200' : 'bg-slate-100 text-slate-600'}`}>{x}</div>
      ))}
    </div>
    {ROWS.map(([label, vals]) => (
      <div key={label} className="grid grid-cols-[1.4fr_repeat(3,1fr)] items-center gap-2 border-b border-slate-50 py-2.5 last:border-0">
        <span className="text-xs sm:text-sm text-slate-600">{label}</span>
        {vals.map((v, i) => <div key={i} className="text-center"><Cell v={v} /></div>)}
      </div>
    ))}
    <p className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800">Solution A fits your clinic on 4 of 4 checks</p>
  </div>
);

export const CompareConfidence = () => (
  <section className="py-12 md:py-16 bg-white" data-testid="home-compare-confidence">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 mb-4">
            <Scale className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-semibold text-orange-700">Compare with confidence</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">See up to 4 solutions side by side</h2>
          <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
            Stop juggling vendor websites. Put your shortlist in one view and see what actually fits your clinic.
          </p>
          <ul className="mt-6 space-y-2.5">
            {POINTS.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                <Check className="mt-0.5 w-5 h-5 text-emerald-500 shrink-0" />{item}
              </li>
            ))}
          </ul>
          <Link to="/marketplace" className="mt-8 inline-block w-full sm:w-auto" data-testid="home-compare-start">
            <Button size="lg" className="w-full sm:w-auto bg-orange-600 hover:bg-orange-500 text-white px-7 py-6 text-base shadow-lg shadow-orange-600/25 transition-transform hover:scale-[1.02]">
              Start comparing <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
        <Preview />
      </div>
    </div>
  </section>
);
